/**
 * useStateSync.js
 * Synchronizes state between component and Pinia store
 */

import { ref, computed, watch, nextTick, toRef } from 'vue'
import { useMainStore } from '@/stores/main'
import { waitForMap } from '../utils/map-helpers'

export function useStateSync(map, shapeRegistry, setCursor) {
  const mainStore = useMainStore()
  const currentInitedShape = ref(null)

  const currentShape = computed(() => mainStore.currentShape)
  const mapCenter = toRef(mainStore, 'mapCenter')
  const mapZoom = toRef(mainStore, 'mapZoom')
  const mode = toRef(mainStore, 'mode')

  /**
   * Watch current shape in store and sync to component
   */
  watch(currentShape, (shape) => {
    currentInitedShape.value = shapeRegistry.getShapeById(shape ? shape.indexID : null)
  }, { immediate: true })

  /**
   * Watch current inited shape and propagate selection to all shapes
   */
  watch(currentInitedShape, (initedShape, oldInitedShape) => {
    shapeRegistry.initedShapes.value.forEach(shape => shape.setGlobalSelectedShape(initedShape))

    if (initedShape) {
      initedShape.setMode(mode.value)
    } else {
      setCursor()
    }

    if (!oldInitedShape || (initedShape && initedShape.indexID !== oldInitedShape.indexID)) {
      mainStore.setToolbarsHidden(false)
    }
  })

  /**
   * Watch map center changes and pan map if needed
   */
  watch(mapCenter, async (newLatLng) => {
    if (newLatLng && newLatLng.lat && newLatLng.lng && !newLatLng.fromMap) {
      if (newLatLng.fromSave) {
        await waitForMap(map)

        nextTick(() => {
          map.value.setCenter(newLatLng)

          setTimeout(() => {
            const bounds = shapeRegistry.getShapeBounds()
            if (bounds) {
              map.value.fitBounds(bounds, 0)
            }
          }, 1000)
        })
      } else {
        console.log('mapcenter')
        if (map.value) {
          map.value.setCenter(newLatLng)
        }
      }
    }
  })

  /**
   * Watch map zoom changes and propagate to shapes
   */
  watch(mapZoom, (newZoom) => {
    if (!newZoom.fromMap && newZoom.number) {
      // Note: map zoom sync is commented out in original
      // this.map.setZoom(newZoom.number)
    }

    shapeRegistry.initedShapes.value.forEach(shape => {
      shape.setMapZoom(newZoom.number)
    })
  }, { immediate: true })

  return {
    currentInitedShape
  }
}
